import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DialogContent from '@mui/material/DialogContent';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button, Switch } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { InformationBanner } from 'src/components/molecules';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';
import { useEditUserModal } from 'src/hooks/cases/useEditUserModal';
import {
  PermissionsType,
  SystemRolesName,
  SystemRolesType,
} from '../../../api';
import * as Types from '../../../types';
import { FormState, State, User } from '../../../types';
import { ToolTip } from '../../atomsLegacy/ToolTip';
import { AppStatusSection } from '../../moleculesLegacy/AppStatusSection';
import UserTextFields from '../../moleculesLegacy/UserTextFields';
import {
  handleSystemRolesToggle,
  handleToggle as toggleHandler,
  useOnTextBoxChange,
} from './EditUserModal.helpers';
import {
  BottomBar,
  ButtonsContainer,
  DeactivationContainer,
  DeactivationText,
  LabelAndToggle,
  ModalBody,
  ModalBodyRight,
  ModalLabelDescription,
  ModalTitle,
  PermissionsLabel,
  StyledDialog,
  ToolTipWrapper,
} from './EditUserModal.styles';

interface Props {
  show: boolean;
  handleClose: () => void;
  inviteSingleUser?: (id: number) => void;
  selectedUser: Partial<User> | null;
  openDeactivateDialog?: () => void;
  openDeactivateNowDialog?: () => void;
  onSuccess?: () => void;
}

export const EditUserModal = ({
  selectedUser,
  show,
  handleClose,
  openDeactivateDialog,
  openDeactivateNowDialog,
  inviteSingleUser,
  onSuccess,
}: Props) => {
  const {
    user,
    setUser,
    validation,
    setValidation,
    isSaveDisabled,
    disableUserDetails,
    putUser,
    userStatus,
    remainingDays,
    editablePermissions,
    editableSuperPermissions,
    editableAdminPermissions,
    currentUser,
    isRemovedDraftRelatedRole,
    isLoading,
  } = useEditUserModal({
    selectedUser,
    show,
    handleClose,
    onSuccess,
  });
  const features = useSelector((state: State) => state.features);
  const organisationRoles = useSelector(
    (state: State) => state.user.organisationRoles,
  );

  const isSlackEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.SLACK_REPORTING,
  );
  const isAppEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.APP_REPORTING,
  );

  const isAppOrSlackEnabledForOrg = isAppEnabled || isSlackEnabled;

  const adminPermission = organisationRoles?.filter(
    (role) => role.name === SystemRolesName.ADMIN,
  )[0];
  const useOnTextBoxChangePopulated = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => useOnTextBoxChange(event, user, setUser, validation, setValidation);

  const handleToggle = (permissions: PermissionsType) =>
    toggleHandler(permissions, currentUser, user, setUser);

  const handleRolesToggle = (permission: SystemRolesType) =>
    handleSystemRolesToggle(
      permission,
      currentUser,
      user,
      adminPermission,
      setUser,
    );

  const disabledRolesManagement = (role: string) => {
    switch (role) {
      case SystemRolesName.ADMIN:
        return !editableAdminPermissions;
      case SystemRolesName.SUPER:
        return !editableSuperPermissions;
      case SystemRolesName.COI_SUPER:
        return !editableSuperPermissions;
      default:
        return !editablePermissions;
    }
  };

  return (
    <StyledDialog maxWidth={false} open={show} data-testid={'edit-user-modal'}>
      <DialogContent>
        <ModalTitle>
          {selectedUser && selectedUser.name ? 'Edit User' : 'Add User'}
        </ModalTitle>
        <ModalBody>
          <UserTextFields
            user={user}
            validation={validation}
            onTextboxChange={useOnTextBoxChangePopulated}
            disabled={disableUserDetails}
          />
          <ModalBodyRight>
            {userStatus === 'EXPIRING' && (
              <DeactivationContainer>
                <DeactivationText>
                  This user has been flagged for deactivation in
                  <strong> {remainingDays} </strong>
                  days
                </DeactivationText>
                <ToolTipWrapper>
                  <ToolTip text="Once deactivated, the employee will no longer be able to access the Vault App or the Vault Portal.">
                    <HelpOutlineIcon />
                  </ToolTip>
                </ToolTipWrapper>
              </DeactivationContainer>
            )}
            {isAppOrSlackEnabledForOrg && (
              <AppStatusSection
                user={user}
                handleClose={handleClose}
                openDeactivateDialog={openDeactivateDialog}
                openDeactivateNowDialog={openDeactivateNowDialog}
                inviteSingleUser={inviteSingleUser}
                handleToggle={() => handleToggle(PermissionsType.APP)}
                selectedUser={selectedUser}
              />
            )}
            <PermissionsLabel>Permissions</PermissionsLabel>

            <>
              {organisationRoles
                ?.filter((role) => role.name !== 'DEFAULT ROLE app')
                .map((role) => {
                  return (
                    <div key={role.id}>
                      <LabelAndToggle>
                        <label>{role.label ?? role.name}</label>
                        <Switch
                          onClick={() => handleRolesToggle(role)}
                          data-testid={`${role.label
                            ?.toLocaleLowerCase()
                            .split(' ')
                            .join('-')}-toggle`}
                          checked={Boolean(
                            user.SystemRoles?.find(
                              (userRole) => userRole.id === role.id,
                            ),
                          )}
                          disabled={disabledRolesManagement(role.name)}
                        />
                      </LabelAndToggle>
                      <ModalLabelDescription
                        data-testid={`${role.label
                          ?.toLocaleLowerCase()
                          .split(' ')
                          .join('-')}-toggle-text`}
                      >
                        {role.description}
                      </ModalLabelDescription>
                    </div>
                  );
                })}
            </>
          </ModalBodyRight>
        </ModalBody>
        <BottomBar>
          {isRemovedDraftRelatedRole && (
            <InformationBanner
              data-testid="removing-role-warning"
              state={FormState.WARNING}
              title="Are you sure?"
              description="Removing this System Role from this user will result in the deletion of any existing 
              draft reports."
              $descriptionLineDrop={true}
            />
          )}
          <ButtonsContainer>
            <Button
              variant="secondary"
              size="medium"
              data-testid="user-cancel-button"
              onClick={handleClose}
              width="120px"
            >
              Cancel
            </Button>
            <SpinnerButton
              variant="primary"
              size="medium"
              data-testid="user-save-button"
              onClick={putUser}
              disabled={isSaveDisabled}
              width="120px"
              loading={isLoading}
            >
              <>
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-save"
                />
                Save
              </>
            </SpinnerButton>
          </ButtonsContainer>
        </BottomBar>
      </DialogContent>
    </StyledDialog>
  );
};
